import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { AdminLayout, inp } from "@/pages/admin/adminShared";
import { formatDateShort } from "@/lib/format";
import { AlertTriangle, Users, Send, FlaskConical } from "lucide-react";

const GSM_EXT = new Set(["^", "{", "}", "\\", "[", "~", "]", "|", "€"]);
function smsInfo(text) {
  text = text || "";
  const gsmBasic = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà^{}\\[~\]|€]*$/;
  const isUnicode = !gsmBasic.test(text);
  let length;
  if (isUnicode) length = [...text].length;
  else length = [...text].reduce((n, c) => n + (GSM_EXT.has(c) ? 2 : 1), 0);
  const single = isUnicode ? 70 : 160, concat = isUnicode ? 67 : 153;
  const segments = length === 0 ? 0 : length <= single ? 1 : Math.ceil(length / concat);
  return { chars: [...text].length, encoding: isUnicode ? "unicode" : "gsm", segments };
}

export default function ManageSMS() {
  const { user } = useAuth();
  const [scope, setScope] = useState("all");
  const [classIds, setClassIds] = useState([]);
  const [venueIds, setVenueIds] = useState([]);
  const [body, setBody] = useState("");
  const [templateKey, setTemplateKey] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [alsoAnnounce, setAlsoAnnounce] = useState(false);

  const publishAnnouncement = async () => {
    const scopeMap = { all: "public", classes: "class", venues: "venue" };
    try {
      await api.post("/admin/announcements", {
        title: (templates || []).find((t) => t.key === templateKey)?.name || "Club announcement",
        message: body, category: "important", scope: scopeMap[scope] || "public",
        class_ids: classIds, venue_ids: venueIds, active: true,
      });
      toast.success("Website announcement published (no SMS sent)");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const { data: config } = useQuery({ queryKey: ["sms-config"], queryFn: async () => (await api.get("/admin/sms/config")).data });
  const { data: templates } = useQuery({ queryKey: ["sms-templates"], queryFn: async () => (await api.get("/admin/sms/templates")).data });
  const { data: classes } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/classes")).data });
  const { data: campaigns, refetch: refetchCampaigns } = useQuery({ queryKey: ["sms-campaigns"], queryFn: async () => (await api.get("/admin/sms/campaigns")).data });

  const venues = useMemo(() => {
    const m = {}; (classes || []).forEach((c) => { if (c.venue?.id) m[c.venue.id] = c.venue.name; });
    return Object.entries(m).map(([id, name]) => ({ id, name }));
  }, [classes]);

  const info = smsInfo(body);
  const audiencePayload = { scope, class_ids: classIds, venue_ids: venueIds };
  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const applyTemplate = (key) => { setTemplateKey(key); const t = (templates || []).find((x) => x.key === key); if (t) setBody(t.body); };

  const checkAudience = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/sms/audience", audiencePayload); setResult({ audience: data }); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const dryRun = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/sms/dry-run", { ...audiencePayload, body, template_key: templateKey }); setResult(data); refetchCampaigns(); toast.success("Dry-run complete — no messages sent"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <AdminLayout role={user?.role} title="Emergency SMS" subtitle="Compose and preview emergency alerts. Sending is disabled — dry-run only.">
      <div className="mb-5 flex items-start gap-2 rounded-2xl border-2 border-[#C85A32]/40 bg-[#FBEBE4]/60 p-4" data-testid="sms-drymode-banner">
        <FlaskConical size={20} className="mt-0.5 shrink-0 text-[#B04B26]" />
        <div className="text-sm text-[#5C4A42]">
          <p className="font-bold text-[#2D1E18]">Dry-run mode {config && !config.live_enabled ? "(live sending disabled)" : ""}</p>
          <p>No SMS are sent and no ClickSend contacts are touched. Everything here is a safe preview. Live sending stays off until it is enabled on the backend and separately approved.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Compose */}
        <div className="space-y-5">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-bold text-[#2D1E18]">Message</h2>
            <label className="mt-3 block text-sm font-semibold text-[#2D1E18]">Template</label>
            <select className={`${inp} mt-1`} value={templateKey} onChange={(e) => applyTemplate(e.target.value)} data-testid="sms-template-select">
              <option value="">— Choose a template —</option>
              {(templates || []).map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
            </select>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} data-testid="sms-body"
              className={`${inp} mt-3`} placeholder="Type your emergency message…" />
            <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-[#6E5B51]" data-testid="sms-counter">
              <span>{info.chars} chars</span>
              <span>{info.segments} segment{info.segments === 1 ? "" : "s"}</span>
              <span>Encoding: {info.encoding.toUpperCase()}{info.encoding === "unicode" ? " (Unicode — fewer chars per segment)" : ""}</span>
            </div>
            <label className="mt-4 flex items-start gap-2 text-sm text-[#5C4A42]">
              <input type="checkbox" checked={alsoAnnounce} onChange={(e) => setAlsoAnnounce(e.target.checked)} className="mt-1" data-testid="sms-publish-announcement" />
              <span>Also publish this as a <strong>website announcement</strong> (works independently of ClickSend — publishing never sends SMS).</span>
            </label>
            {alsoAnnounce && (
              <button onClick={publishAnnouncement} disabled={!body.trim()} data-testid="sms-create-announcement"
                className="mt-2 rounded-xl border border-[#C85A32] px-4 py-2 text-sm font-bold text-[#B04B26] hover:bg-[#C85A32] hover:text-white disabled:opacity-60">Publish website announcement now</button>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-bold text-[#2D1E18]">Audience</h2>
            <div className="mt-3 flex gap-2">
              {["all", "classes", "venues"].map((s) => (
                <button key={s} onClick={() => setScope(s)} data-testid={`sms-scope-${s}`}
                  className={`rounded-full px-4 py-2 text-sm font-semibold capitalize ${scope === s ? "bg-[#C85A32] text-white" : "border border-border text-[#5C4A42]"}`}>
                  {s === "all" ? "All eligible members" : s}
                </button>
              ))}
            </div>
            {scope === "classes" && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="sms-class-picker">
                {(classes || []).map((c) => (
                  <button key={c.id} onClick={() => toggle(classIds, setClassIds, c.id)}
                    className={`rounded-lg border px-3 py-1.5 text-sm ${classIds.includes(c.id) ? "border-[#C85A32] bg-[#FBEBE4]" : "border-border"}`}>{c.name}</button>
                ))}
              </div>
            )}
            {scope === "venues" && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="sms-venue-picker">
                {venues.map((v) => (
                  <button key={v.id} onClick={() => toggle(venueIds, setVenueIds, v.id)}
                    className={`rounded-lg border px-3 py-1.5 text-sm ${venueIds.includes(v.id) ? "border-[#C85A32] bg-[#FBEBE4]" : "border-border"}`}>{v.name}</button>
                ))}
              </div>
            )}
            <div className="mt-4 flex gap-3">
              <button onClick={checkAudience} disabled={busy} data-testid="sms-check-audience" className="inline-flex items-center gap-2 rounded-xl border border-[#C85A32] px-4 py-2.5 font-bold text-[#B04B26] hover:bg-[#C85A32] hover:text-white"><Users size={16} /> Check recipients</button>
              <button onClick={dryRun} disabled={busy || !body.trim()} data-testid="sms-dry-run" className="inline-flex items-center gap-2 rounded-xl bg-[#C85A32] px-4 py-2.5 font-bold text-white hover:bg-[#B04B26] disabled:opacity-60"><Send size={16} /> Run dry-run (no send)</button>
            </div>
          </div>
        </div>

        {/* Preview / result */}
        <div className="space-y-5">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-bold text-[#2D1E18]">Preview</h2>
            <div className="mt-3 rounded-xl bg-[#2D1E18] p-4 text-sm text-[#FBEBE4]" data-testid="sms-preview">{body || <span className="text-[#6E5B51]">Your message will appear here…</span>}</div>
          </div>

          {result?.audience && (
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="sms-result">
              <h2 className="font-display text-lg font-bold text-[#2D1E18]">Recipients &amp; estimate</h2>
              <dl className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><dt className="text-[#6E5B51]">Opted in</dt><dd className="font-semibold">{result.audience.opted_in}</dd></div>
                <div className="flex justify-between"><dt className="text-[#6E5B51]">Eligible (valid mobile)</dt><dd className="font-semibold">{result.audience.eligible_mobile}</dd></div>
                <div className="flex justify-between"><dt className="text-[#6E5B51]">Opted in but no mobile</dt><dd className="font-semibold text-[#9A5B1E]">{result.audience.ineligible_no_mobile}</dd></div>
                <div className="flex justify-between"><dt className="text-[#6E5B51]">Unique recipients (de-duplicated)</dt><dd className="font-bold text-[#2D1E18]" data-testid="sms-recipient-count">{result.audience.unique_recipients}</dd></div>
                <div className="flex justify-between"><dt className="text-[#6E5B51]">Verified for live sending</dt><dd className="font-semibold text-[#991B1B]">{result.audience.verified_for_live}</dd></div>
                {result.est_cost_gbp !== undefined && <div className="flex justify-between border-t border-border pt-2"><dt className="text-[#6E5B51]">Estimated cost ({result.message?.segments} seg × {result.recipient_count})</dt><dd className="font-bold text-[#2D1E18]" data-testid="sms-est-cost">£{result.est_cost_gbp.toFixed(2)}</dd></div>}
              </dl>
              <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-[#FEF6EC] px-3 py-2 text-xs text-[#9A5B1E]"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {result.audience.suppression_status}. Actual ClickSend charges may vary.</p>
              {result.warnings?.map((w, i) => <p key={i} className="mt-2 text-xs font-semibold text-[#991B1B]">• {w}</p>)}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <h2 className="mb-3 font-display text-lg font-bold text-[#2D1E18]">Dry-run history</h2>
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-[#6E5B51]"><tr><th className="p-3">When</th><th className="p-3">By</th><th className="p-3">Scope</th><th className="p-3">Recipients</th><th className="p-3">Segments</th><th className="p-3">Est. cost</th><th className="p-3">Mode</th></tr></thead>
            <tbody>
              {(campaigns || []).map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0" data-testid={`sms-campaign-${c.id}`}>
                  <td className="p-3 text-[#6E5B51]">{formatDateShort(c.created_at)}</td>
                  <td className="p-3">{c.created_by_name}</td>
                  <td className="p-3 capitalize">{c.scope}</td>
                  <td className="p-3">{c.recipient_count}</td>
                  <td className="p-3">{c.segments}</td>
                  <td className="p-3">£{(c.est_cost_gbp || 0).toFixed(2)}</td>
                  <td className="p-3"><span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-[#5C4A42]">{c.mode}</span></td>
                </tr>
              ))}
              {!campaigns?.length && <tr><td colSpan={7} className="p-6 text-center text-sm text-[#6E5B51]">No dry-runs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
